import React, { useEffect, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import Dropdown from "components/dropdown";
import { FiAlignJustify, FiSearch } from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import {
  IoMdNotificationsOutline,
  IoMdInformationCircleOutline,
} from "react-icons/io";
import avatar from "assets/img/avatars/avatar4.png";
import { db } from "services/firebase";
import { useAuth } from "context/AuthContext";
import { collection, query, getDoc, getDocs, doc, updateDoc, addDoc } from "firebase/firestore";
import { useNotificationHandler } from "context/NotificationHandler"; 
import Modal from "components/modal/Modal";

type AppNotification = {
  id: string;
  title: string;
  description: string;
  icon: JSX.Element;
  route: string;
};

const PhoneNumberModal = ({ onClose, user }: { onClose: () => void; user: any }) => {
  const [phoneNumber, setPhoneNumber] = useState("");

  useEffect(() => {
    const fetchPhoneNumber = async () => {
      if (!user?.uid) return;
      const collectionRef = collection(db, `clientes/${user.uid}/assinatura`);
      const q = query(collectionRef);
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const data = querySnapshot.docs[0].data();
        if (data.Numero) {
          setPhoneNumber(data.Numero.replace("+55", ""));
        }
      }
    };
    fetchPhoneNumber();
  }, [user]);

  const handleSavePhoneNumber = useCallback(async () => {
    if (!user?.uid) return;

    const phoneRegex = /^\d{11}$/;
    if (!phoneRegex.test(phoneNumber)) {
      alert("Formato de número inválido. Por favor, insira DDD + 9 dígitos (ex: 11987654321).");
      return;
    }

    const collectionRef = collection(db, `clientes/${user.uid}/assinatura`);
    const formattedNumber = `+55${phoneNumber}`;

    try {
      const q = query(collectionRef);
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docId = querySnapshot.docs[0].id;
        const docRef = doc(db, `clientes/${user.uid}/assinatura`, docId);
        await updateDoc(docRef, { Numero: formattedNumber });
      } else {
        const newDocData = {
          Data: new Date().toISOString().split("T")[0],
          Status: "Ativado" as const,
          Numero: formattedNumber,
        };
        await addDoc(collectionRef, newDocData);
      }
      onClose();
    } catch (error) {
      console.error("Erro ao salvar número de celular:", error);
      alert("Ocorreu um erro ao salvar o número. Tente novamente.");
    }
  }, [user, phoneNumber, onClose]);

  return ReactDOM.createPortal(
    <Modal onClose={onClose}>
      <h2 className="text-lg font-bold mb-2">Modificar Número de Celular</h2>
      <input
        className="w-full border rounded p-2 mb-2"
        placeholder="Insira DDD + 9 dígitos"
        value={phoneNumber}
        onChange={(e) => setPhoneNumber(e.target.value)}
        maxLength={11}
      />
      <button
        className="mt-2 px-3 py-1 bg-[#CF4949] font-bold text-white transition duration-200 hover:bg-[#B84343] active:bg-[#B84343] rounded"
        onClick={handleSavePhoneNumber}
      >
        Salvar
      </button>
    </Modal>,
    document.body
  );
};


const Navbar = (props: {
  onOpenSidenav: () => void;
  brandText: string;
  secondary?: boolean | string;
}) => {
  const { onOpenSidenav, brandText } = props;
  const [avatarSrc, setAvatarSrc] = useState(avatar);
  const { user, logout } = useAuth();
  const email = user?.email?.split("@")[0] || "Usuário";
  const navigate = useNavigate();
  
  const { visibleNotifications, dismissNotification, dismissAllNotifications } = useNotificationHandler();
  const [showPhoneNumberModal, setShowPhoneNumberModal] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setAvatarSrc(avatar);
      return;
    }
    const fetchImages = async () => {
      try {
        const userRef = doc(db, `clientes/${user.uid}/perfil`, "data");
        const docSnap = await getDoc(userRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.avatarUrl) setAvatarSrc(data.avatarUrl);
        } else {
          setAvatarSrc(avatar);
        }
      } catch (error) {
        console.error("Erro ao buscar imagens do perfil:", error);
        setAvatarSrc(avatar);
      }
    };
    fetchImages();
  }, [user]);

  const handleMarkAllAsRead = () => {
    dismissAllNotifications();
  };

  const handleClickNotification = (notif: AppNotification) => {
    dismissNotification(notif.id);
    if (notif.route) {
      navigate(notif.route);
    }
  };

  const handleLogout = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    logout();
  };

  return (
    <nav className="sticky top-4 z-40 flex flex-row flex-wrap items-center justify-between rounded-xl bg-white/10 p-2 backdrop-blur-xl">
      <div className="ml-[6px]">
        <div className="h-6 w-[224px] pt-1">
          <a
            className="text-sm font-normal text-navy-700 hover:underline"
            href=" "
          >
            Páginas
            <span className="mx-1 text-sm text-navy-700 hover:text-navy-700">
              {" "}
              /{" "}
            </span>
          </a>
          <Link
            className="text-sm font-normal capitalize text-navy-700 hover:underline"
            to="#"
          >
            {brandText}
          </Link>
        </div>
        <p className="shrink text-[33px] capitalize text-navy-700">
          <Link to="#" className="font-bold capitalize hover:text-navy-700">
            {brandText}
          </Link>
        </p>
      </div>

      <div className="relative mt-[3px] flex h-[61px] w-[355px] flex-grow items-center justify-around gap-2 rounded-full bg-white px-2 py-2 shadow-xl shadow-shadow-500 md:w-[365px] md:flex-grow-0 md:gap-1 xl:w-[365px] xl:gap-2">
        <div className="flex h-full items-center rounded-full bg-lightPrimary text-navy-700 xl:w-[225px]">
          <p className="pl-3 pr-2 text-xl">
            <FiSearch className="h-4 w-4 text-gray-400" />
          </p>
          <input
            type="text"
            placeholder="Pesquisar..."
            className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 sm:w-fit"
          />
        </div>

        <span
          className="flex cursor-pointer text-xl text-gray-600 xl:hidden"
          onClick={onOpenSidenav}
        >
          <FiAlignJustify className="h-5 w-5" />
        </span>

        <Dropdown
          button={
            <div className="relative cursor-pointer">
              <IoMdNotificationsOutline className="h-4 w-4 text-gray-600" />
              {visibleNotifications.length > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#CF4949] px-[5px] text-[10px] font-bold leading-none text-white">
                  {visibleNotifications.length}
                </span>
              )}
            </div>
          }
          animation="origin-[65%_0%] md:origin-top-right transition-all duration-300 ease-in-out"
          children={
            <div className="flex w-[360px] flex-col gap-3 rounded-[20px] bg-white p-4 shadow-xl shadow-shadow-500 sm:w-[460px]">
              <div className="flex items-center justify-between">
                <p className="text-base font-bold text-navy-700">Notificações</p>
                <button
                  className="text-sm font-bold text-navy-700 hover:opacity-80"
                  onClick={handleMarkAllAsRead}
                >
                  Marcar todos como lidos
                </button>
              </div>

              {visibleNotifications.length === 0 && (
                <div className="rounded-xl border border-gray-200 p-4 text-center text-sm text-gray-500">
                  Sem novas notificações.
                </div>
              )}

              {visibleNotifications.map((notif) => (
                <button
                  key={`${notif.id}-${notif.title}-${notif.description}`}
                  className="flex w-full items-center"
                  onClick={() => handleClickNotification(notif)}
                >
                  <div className="flex h-full w-[85px] items-center justify-center rounded-xl bg-[#CF4949] py-4 text-2xl text-white">
                    {notif.icon}
                  </div>
                  <div className="ml-2 flex h-full w-full flex-col justify-center rounded-lg px-1 text-sm">
                    <p className="mb-1 text-left text-base font-bold text-gray-900">
                      {notif.title}
                    </p>
                    <p className="font-base text-left text-xs text-gray-900">
                      {notif.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          }
          classNames={"py-2 top-4 -left-[230px] md:-left-[440px] w-max"}
        />

        <Dropdown
          button={
            <p className="cursor-pointer">
              <IoMdInformationCircleOutline className="h-4 w-4 text-gray-600" />
            </p>
          }
          children={
            <div className="flex w-[350px] flex-col gap-2 rounded-[20px] bg-white p-4 shadow-xl shadow-shadow-500">
              <a
                target="blank"
                href="mailto:controlfoods@hotmail.com"
                className="px-full flex items-center justify-center rounded-xl bg-[#CF4949] py-[12px] text-base font-bold text-white transition duration-200 hover:bg-[#B84343] active:bg-[#B84343]"
              >
                Solicitar ajuda
              </a>
              <a
                target="blank"
                href="mailto:controlfoods@hotmail.com"
                className="px-full flex items-center justify-center rounded-xl border py-[11px] font-bold text-[#CF4949] transition duration-200 hover:bg-gray-200 hover:text-[#CF4949]"
              >
                Relatar um problema
              </a>
            </div>
          }
          classNames={"py-2 top-6 -left-[250px] md:-left-[330px] w-max"}
          animation="origin-[75%_0%] md:origin-top-right transition-all duration-300 ease-in-out"
        />

        <Dropdown
          button={<img className="h-10 w-10 rounded-full" src={avatarSrc} alt="Avatar do usuário" />}
          children={
            <div className="flex h-40 w-56 flex-col justify-start rounded-[20px] bg-white bg-cover bg-no-repeat shadow-xl shadow-shadow-500">
              <div className="mt-3 ml-4">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-navy-700">👋 Olá, {email}</p>
                </div>
              </div>
              <div className="mt-3 h-px w-full bg-gray-200" />
              <div className="mt-3 ml-4 flex flex-col">
                <button onClick={() => setShowPhoneNumberModal(true)} className="text-left text-sm text-gray-800 hover:font-bold">
                  Modificar número de Celular
                </button>
                <Link to="/admin/perfil" className="mt-2 text-left text-sm text-gray-800 hover:font-bold">
                  Configurações de Perfil
                </Link>
                <a
                  href="/auth/sign-in"
                  onClick={handleLogout}
                  className="mt-2 text-left text-sm text-red-500 hover:font-bold"
                >
                  Deslogar
                </a>
              </div>
            </div>
          }
          classNames={"py-2 top-8 -left-[180px] w-max"}
        />
      </div>

      {showPhoneNumberModal && <PhoneNumberModal onClose={() => setShowPhoneNumberModal(false)} user={user} />}
    </nav>
  );
};

export default Navbar;
